/**
 * LegalTemplates PDF Editor — contextual properties panel
 *
 * A floating card at the top-right of the canvas that shows what the current
 * selection or tool can do. On wide screens the toolbar's size/colour/font
 * controls move into the panel so they sit next to the thing they change; on
 * phones and narrow windows they stay in the sub-bar and the panel never
 * renders. Nothing here owns state: it reads `state` and calls the existing
 * selection helpers.
 */
import { deleteSelectedAnnotation, getAnnotationTypeLabel, getSelectedAnnotation } from './annotations.js';
import { repositionAnchoredChrome } from './shell.js';
import { editToolLabels, state, toolHints } from './context.js';

function escapeHtml(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

const panelEl = document.getElementById('pdf-props-panel');
const titleEl = document.getElementById('pdf-props-title');
const bodyEl = document.getElementById('pdf-props-body');
const controlsHostEl = document.getElementById('pdf-props-controls');
const deleteButton = document.getElementById('pdf-props-delete');
const closeButton = document.getElementById('pdf-props-close');
const controlsEl = document.querySelector('.pdf-editor-toolbar-controls');
const controlsHomeEl = controlsEl ? controlsEl.parentElement : null;

// Tools whose properties are worth a panel when nothing is selected.
const TOOLS_WITH_PROPERTIES = new Set(['text', 'replace', 'shape', 'highlight', 'underline', 'draw', 'erase', 'redact', 'comment', 'image']);
const SELECTION_NOTES = {
    text: 'Drag to move. Double-click to change the words. Font, size and colour apply to this text.',
    comment: 'Drag the pin to move it. Double-click the pin to edit the note.',
    image: 'Drag to move. Use the corner handle to resize.',
    'edit-block': 'Double-click to edit the paragraph again. Delete restores the original text.',
    'replace-text': 'Delete restores the original line.',
};

// Module-private state (modules keep mutable values in objects, never in a
// top-level `let`; see PdfOnlineEditorShellTest).
const propsState = { dismissed: null };

export function isPropsPanelViewport() {
    return window.matchMedia('(min-width: 1100px)').matches;
}

function placeControls(inPanel) {
    if (!controlsEl || !controlsHostEl || !controlsHomeEl) {
        return;
    }
    if (inPanel) {
        if (controlsEl.parentElement !== controlsHostEl) {
            controlsHostEl.appendChild(controlsEl);
        }
    } else if (controlsEl.parentElement !== controlsHomeEl) {
        // Back in the sub-bar, ahead of the form-fill bar it used to precede.
        const formFillBar = controlsHomeEl.querySelector('#pdf-form-fill-bar');
        controlsHomeEl.insertBefore(controlsEl, formFillBar || null);
    }
}

function describeContext() {
    if (state.activeSuiteTool !== 'edit') {
        return null;
    }
    const selected = getSelectedAnnotation();
    if (selected) {
        const type = String(selected.type || '');
        const label = getAnnotationTypeLabel(selected);
        let note = SELECTION_NOTES[type] || 'Press Delete to remove it.';
        if (type === 'comment' && selected.text) {
            note = `<span class="pdf-props-quote">${escapeHtml(String(selected.text))}</span>${note}`;
        }
        return {
            key: `selection:${selected.id}`,
            title: `${label} selected`,
            body: note,
            controls: type === 'text',
            canDelete: true,
            tool: state.activeTool,
        };
    }
    const tool = state.activeTool;
    if (!TOOLS_WITH_PROPERTIES.has(tool)) {
        return null;
    }
    return {
        key: `tool:${tool}`,
        title: editToolLabels[tool] || tool,
        body: toolHints[tool] || '',
        controls: true,
        canDelete: false,
        tool,
    };
}

function hidePanel() {
    if (!panelEl) {
        return;
    }
    panelEl.hidden = true;
    panelEl.removeAttribute('data-context');
    panelEl.removeAttribute('data-active-tool');
}

export function syncPropsPanel() {
    if (!panelEl) {
        return;
    }
    const wide = isPropsPanelViewport();
    const context = wide ? describeContext() : null;

    // The controls only live in the panel while it is on screen; otherwise
    // they sit in the sub-bar where the phone/tablet layout expects them.
    const showPanel = !!(context && propsState.dismissed !== context.key);
    placeControls(showPanel);

    if (!showPanel) {
        hidePanel();
        return;
    }

    panelEl.hidden = false;
    repositionAnchoredChrome();
    panelEl.dataset.context = context.key.startsWith('selection') ? 'selection' : 'tool';
    panelEl.dataset.activeTool = context.tool || '';
    if (titleEl) {
        titleEl.textContent = context.title;
    }
    if (bodyEl) {
        bodyEl.innerHTML = context.body;
    }
    if (controlsHostEl) {
        controlsHostEl.hidden = !context.controls;
    }
    if (deleteButton) {
        deleteButton.hidden = !context.canDelete;
    }
}

export function bindPropsPanel() {
    if (!panelEl) {
        return;
    }
    if (closeButton) {
        closeButton.addEventListener('click', () => {
            const context = describeContext();
            propsState.dismissed = context ? context.key : null;
            syncPropsPanel();
        });
    }
    if (deleteButton) {
        deleteButton.addEventListener('click', () => {
            deleteSelectedAnnotation();
            syncPropsPanel();
        });
    }
    // Keep clicks inside the panel from reaching the document-level handlers
    // that clear selections or close menus.
    panelEl.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
    });

    let resizeTimer = null;
    window.addEventListener('resize', () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(syncPropsPanel, 120);
    });

    syncPropsPanel();
}

export function resetPropsPanelDismissal() {
    propsState.dismissed = null;
}
